import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { wrapper } from "@/store";
import { setMeta, setOg, setPath, setTitle } from "@/store/slices/helmet";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  LoadScript,
} from "@react-google-maps/api";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import Layout from "../layout/layout";
import styles from "../styles/store.module.scss";
import Select from "react-select";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const libraries: any[] = ["geometry"];

const StoreLocator = () => {
  const [map, setMap] = useState<any>(null);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const [center] = useState<any>({
    lat: 21.767,
    lng: 78.871,
  });
  const [openInfo, setOpenInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<any[]>([]);
  const [state, setState] = useState<any>(null);
  const [city, setCity] = useState<any>(null);
  const [store, setStore] = useState<any>(null);
  const [locationError, setLocationError] = useState<string>("");

  const states = useMemo(
    () => Array.from(new Set(stores.map((v) => v.store_state))),
    [stores]
  );

  const cities = useMemo(
    () =>
      Array.from(
        new Set(
          stores
            .filter((v) => v.store_state == state?.value)
            .map((v) => v.store_city)
        )
      ),
    [stores, state]
  );

  const storeOptions = useMemo(
    () => stores.filter((v) => v.store_city == city?.value),
    [stores, city]
  );

  const visibleStores = useMemo(() => {
    if (store) {
      return stores.filter((v) => v.store_code == store.value);
    }
    if (city) {
      return stores.filter((v) => v.store_city == city.value);
    }
    if (state) {
      return stores.filter((v) => v.store_state == state.value);
    }
    return stores;
  }, [stores, state, city, store]);

  useEffect(() => {
    loadStoresData();
  }, []);

  useEffect(() => {
    if (visibleStores.length && map) {
      const bounds = new google.maps.LatLngBounds();
      visibleStores.forEach((store) => {
        bounds.extend(
          new google.maps.LatLng(
            parseFloat(store.store_lat),
            parseFloat(store.store_long)
          )
        );
      });
      map.fitBounds(bounds);
    }
  }, [visibleStores, map]);

  const loadStoresData = async () => {
    const { data } = await httpClient.get(endpoints.storesData());
    setStores(data.data);
    setLoading(false);
  };

  const useMyCurrentLocation = () => {
    setState(null);
    setCity(null);
    setStore(null);
    setLocationError("");
    if (window.navigator.geolocation) {
      window.navigator.geolocation.getCurrentPosition(
        (data) => {
          const location: any = new google.maps.LatLng(
            data.coords.latitude,
            data.coords.longitude
          );
          const nearestLocation: any = visibleStores
            .map((store) => ({
              lat: parseFloat(store.store_lat),
              lng: parseFloat(store.store_long),
            }))
            .reduce(function (prev, curr) {
              var cpos = google.maps.geometry.spherical.computeDistanceBetween(
                location,
                curr
              );
              var ppos = google.maps.geometry.spherical.computeDistanceBetween(
                location,
                prev
              );

              return cpos < ppos ? curr : prev;
            });
          if (nearestLocation) {
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(
              new google.maps.LatLng(
                parseFloat(nearestLocation.lat),
                parseFloat(nearestLocation.lng)
              )
            );
            map.fitBounds(bounds);
          }
        },
        (error) => {
          setLocationError(error.message || "An unknown error occurred");
        }
      );
    } else {
      setLocationError("Geolocation is not supported by this browser");
    }
  };

  return (
    <>
      <section className={styles.Store_section}>
        <div className="container">
          <div className="row d-flex justify-content-center">
            <div className="col-md-12">
              <h1 className={styles.explore}>Explore Our Store</h1>
              <div className="row mb-md-0 mb-2">
                <div className="col-md-3">
                  {loading ? (
                    <Skeleton
                      style={{ height: 40 }}
                      className="my-2 my-md-4 pt-1"
                    />
                  ) : (
                    <Select
                      className="my-2 my-md-4 pt-1"
                      placeholder="Select State"
                      isClearable
                      value={state}
                      onChange={(e) => {
                        setState(e);
                        setCity(null);
                        setStore(null);
                      }}
                      options={states.map((city) => ({
                        value: city,
                        label: city,
                      }))}
                    />
                  )}
                </div>
                <div className="col-md-3">
                  {loading ? (
                    <Skeleton
                      style={{ height: 40 }}
                      className="my-2 my-md-4 pt-1"
                    />
                  ) : (
                    <Select
                      className="my-2 my-md-4 pt-1"
                      placeholder="Select City"
                      isClearable
                      value={city}
                      onChange={(e) => {
                        setCity(e);
                        setStore(null);
                      }}
                      options={cities.map((city) => ({
                        value: city,
                        label: city,
                      }))}
                    />
                  )}
                </div>
                <div className="col-md-3">
                  {loading ? (
                    <Skeleton
                      style={{ height: 40 }}
                      className="my-2 my-md-4 pt-1"
                    />
                  ) : (
                    <Select
                      className="my-2 my-md-4 pt-1"
                      placeholder="Select Store"
                      isClearable
                      value={store}
                      onChange={(e) => {
                        setStore(e);
                      }}
                      options={storeOptions.map((store) => ({
                        value: store.store_code,
                        label: store.store_name,
                      }))}
                    />
                  )}
                </div>
                <div className="col-md-3">
                  {loading ? (
                    <Skeleton
                      style={{ height: 40 }}
                      className="my-2 my-md-4 pt-1"
                    />
                  ) : (
                    <>
                      <button
                        role="button"
                        aria-label="USE MY CURRENT LOCATION"
                        className={`${styles.btn} ${
                          locationError ? "mb-2" : ""
                        }`}
                        onClick={useMyCurrentLocation}
                      >
                        USE MY CURRENT LOCATION
                      </button>
                      {locationError && (
                        <span className="text-danger">{locationError}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className={styles.total_store}>
                <strong>
                  {store
                    ? store.label
                    : city
                    ? city?.label
                    : state?.label || "Total"}{" "}
                  {visibleStores.length} Stores
                </strong>
              </div>
            </div>
          </div>
          <div className="mb-4 row">
            <div className="col-md-12">
              <LoadScript
                googleMapsApiKey="AIzaSyCbM-gcKYhz6xomaWyna28XTi3AtCucbrg"
                libraries={libraries}
                loadingElement={<Skeleton style={containerStyle} />}
              >
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={center}
                  zoom={2}
                  onLoad={onLoad}
                  onUnmount={onUnmount}
                >
                  {visibleStores.map((store) => (
                    <Marker
                      key={store.store_code}
                      position={{
                        lat: parseFloat(store.store_lat),
                        lng: parseFloat(store.store_long),
                      }}
                      onClick={() => setOpenInfo(store.store_code)}
                    >
                      {openInfo == store.store_code && (
                        <InfoWindow onCloseClick={() => setOpenInfo(null)}>
                          <div className="container-fluid">
                            <div className="row">
                              <div className="col-md-12">
                                <h5
                                  className="text-center"
                                  style={{
                                    color: "#FF1933",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {store.store_name}
                                </h5>
                                <p>
                                  <i className="bi bi-signpost"></i>{" "}
                                  {store.map_address}
                                </p>
                                <p>
                                  <i className="bi bi-telephone"></i>{" "}
                                  {store.store_phoneno}
                                </p>
                                <p>
                                  <i className="bi bi-envelope"></i>{" "}
                                  {store.store_email}
                                </p>
                                <p>
                                  <i className="bi bi-clock"></i>{" "}
                                  {store.map_timing_text}
                                </p>
                                <button
                                  role="button"
                                  aria-label="Your LOCATION"
                                  className={styles.btn}
                                  onClick={() =>
                                    window.open(
                                      `https://www.google.es/maps/dir/Your+Location/${encodeURIComponent(
                                        store.map_address
                                      )}`,
                                      "_blank"
                                    )
                                  }
                                >
                                  Get Direction
                                </button>
                              </div>
                            </div>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>
                  ))}
                </GoogleMap>
              </LoadScript>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

StoreLocator.getLayout = (page: ReactElement) => {
  return <Layout>{page}</Layout>;
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ resolvedUrl }) => {
      store.dispatch(setPath(resolvedUrl));
      store.dispatch(setTitle("Senco Gold: Best Jewellery Store Near Me"));
      store.dispatch(
        setMeta({
          title: "Senco Gold: Best Jewellery Store Near Me",
          description:
            "Discover elegance at its finest with Senco Gold's best jewellery store near you. Explore exquisite designs crafted for every occasion.",
          keywords: "Best Jewellery Store Near Me",
        })
      );
      store.dispatch(
        setOg({
          type: "website",
          url: `${process.env.NEXT_PUBLIC_APP_URL}${resolvedUrl}`,
          title: "Senco Gold: Best Jewellery Store Near Me",
          description:
            "Discover elegance at its finest with Senco Gold's best jewellery store near you. Explore exquisite designs crafted for every occasion.",
          image: "",
        })
      );
      return {
        props: {},
      };
    }
);

export default StoreLocator;
